import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

const THRESHOLD = 70; // px of pull that triggers a refresh
const MAX = 110;      // the indicator stops following the finger here

/**
 * Pull down at the top of any screen to refresh it — the gesture people expect
 * in an app. It refetches the data on screen (every active query) rather than
 * reloading the page, so drafts and scroll position survive.
 *
 * Stays out of the way of gestures that mean something else: maps (dragging a
 * pin), open dialogs/sheets, text fields, and anything inside a scrolled
 * container. Only touch input starts it, so desktop scrolling is unaffected.
 */
export default function PullToRefresh() {
  const qc = useQueryClient();
  const [pull, setPull] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const start = useRef(null);
  const pullRef = useRef(0);
  const busy = useRef(false);

  useEffect(() => {
    const blocked = (el) => {
      for (let n = el; n && n !== document.body; n = n.parentElement) {
        if (n.closest?.('.leaflet-container, [role="dialog"], input, textarea, select, [data-no-pull]')) return true;
        // A scrolled inner list: pulling should scroll it, not refresh the page.
        if (n.scrollTop > 0 && n.scrollHeight > n.clientHeight) return true;
      }
      return false;
    };

    const onStart = (e) => {
      if (busy.current || e.touches.length !== 1) return;
      if (window.scrollY > 0 || blocked(e.target)) { start.current = null; return; }
      start.current = { y: e.touches[0].clientY, x: e.touches[0].clientX };
    };
    const onMove = (e) => {
      if (!start.current) return;
      const dy = e.touches[0].clientY - start.current.y;
      const dx = Math.abs(e.touches[0].clientX - start.current.x);
      if (dy <= 0 || dx > dy || window.scrollY > 0) {
        if (pullRef.current) { pullRef.current = 0; setPull(0); }
        return;
      }
      // Resistance: the indicator moves slower than the finger.
      const d = Math.min(MAX, dy * 0.5);
      pullRef.current = d;
      setPull(d);
      if (e.cancelable && d > 8) e.preventDefault(); // no page bounce while pulling
    };
    const onEnd = async () => {
      const d = pullRef.current;
      start.current = null;
      pullRef.current = 0;
      if (d < THRESHOLD) { setPull(0); return; }
      busy.current = true;
      setRefreshing(true);
      setPull(THRESHOLD);
      try {
        await Promise.all([
          qc.refetchQueries({ type: 'active' }),
          new Promise((r) => setTimeout(r, 600)), // long enough to see it happen
        ]);
      } finally {
        busy.current = false;
        setRefreshing(false);
        setPull(0);
      }
    };

    window.addEventListener('touchstart', onStart, { passive: true });
    window.addEventListener('touchmove', onMove, { passive: false });
    window.addEventListener('touchend', onEnd);
    window.addEventListener('touchcancel', onEnd);
    return () => {
      window.removeEventListener('touchstart', onStart);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onEnd);
      window.removeEventListener('touchcancel', onEnd);
    };
  }, [qc]);

  if (!pull && !refreshing) return null;
  const ready = pull >= THRESHOLD;
  return (
    <div
      className="pointer-events-none fixed inset-x-0 z-[60] flex justify-center"
      style={{ top: 'calc(env(safe-area-inset-top, 0px) + 8px)', transform: `translateY(${pull - 40}px)`, transition: refreshing || !pull ? 'transform 200ms ease' : 'none' }}
      role="status"
      aria-live="polite"
    >
      <span className="grid h-10 w-10 place-items-center rounded-full bg-surface shadow-lift ring-1 ring-oat-dark">
        <svg
          viewBox="0 0 24 24"
          className={`h-5 w-5 text-brand ${refreshing ? 'animate-spin' : ''}`}
          style={refreshing ? undefined : { transform: `rotate(${ready ? 180 : (pull / THRESHOLD) * 180}deg)` }}
          fill="none"
          stroke="currentColor"
          strokeWidth="2.4"
          strokeLinecap="round"
          aria-hidden="true"
        >
          {refreshing ? <path d="M21 12a9 9 0 1 1-6.2-8.6" /> : <path d="M12 5v14M5 12l7 7 7-7" />}
        </svg>
        <span className="sr-only">{refreshing ? 'Refreshing' : ready ? 'Release to refresh' : 'Pull to refresh'}</span>
      </span>
    </div>
  );
}
